import { useCallback, useEffect, useRef } from 'react';

/**
 * Per-tab scroll-position persistence for consolidated workbenches whose tab
 * panels share a single `overflow-auto` scroll container.
 *
 * When several tab panels live inside one scroll container (the
 * KeepMountedPanel / Radix `TabsContent` pattern used across the consolidated
 * costing, CRM, and quality workbenches), switching tabs would otherwise drop
 * the operator back to the top of a long list — or leave them at the previous
 * tab's offset, which is meaningless for the new tab. This hook records each
 * tab's `scrollTop` and restores it when that tab is re-activated.
 *
 * Mirrors the inline pattern already proven in CostingConfigurationWorkbench:
 * a ref-map keyed by tab id, snapshotted before a switch and restored after the
 * incoming panel renders. Accessibility-additive — it changes no layout, logic,
 * or styling; it only preserves scroll context across tab switches so keyboard
 * and screen-reader users don't lose their place.
 *
 * @example
 * const { scrollRef, onScroll, onTabChange } = useTabScrollPersistence(activeTab);
 * // ...
 * <div ref={scrollRef} onScroll={onScroll} className="overflow-auto">…</div>
 * // and route every tab change through onTabChange:
 * <Tabs value={activeTab} onValueChange={(v) => onTabChange(v, () => setTab(v))}>
 */
export function useTabScrollPersistence<T extends HTMLElement = HTMLDivElement>(
  activeTab: string,
) {
  const scrollRef = useRef<T | null>(null);
  // Saved scrollTop per tab id. A plain object map keyed by the tab string so
  // any tab vocabulary works without the caller declaring the union up front.
  const offsets = useRef<Record<string, number>>({});

  // Record the active tab's offset as the operator scrolls.
  const onScroll = useCallback(() => {
    const el = scrollRef.current;
    if (el) offsets.current[activeTab] = el.scrollTop;
  }, [activeTab]);

  // Snapshot the outgoing tab's offset synchronously *before* the switch so a
  // scroll event mid-switch can't overwrite it with the incoming tab's value,
  // then run the caller's state change. Pass this every tab change.
  const onTabChange = useCallback(
    (_next: string, commit: () => void) => {
      const el = scrollRef.current;
      if (el) offsets.current[activeTab] = el.scrollTop;
      commit();
    },
    [activeTab],
  );

  // Restore the saved offset after the newly-activated tab's panel renders.
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = offsets.current[activeTab] ?? 0;
  }, [activeTab]);

  return { scrollRef, onScroll, onTabChange };
}
